import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Star, BadgePercent } from 'lucide-react';

const allPlans = {
  monthly: [
    { id: "trial", name: "Trial", price: "Free", period: "7 days", description: "Try Ariskool risk-free", maxUsers: 10, popular: false,
      features: ["Up to 10 users", "Leave request & approval", "Basic dashboard", "Email support"] },
    { id: "starter_monthly", name: "Starter", price: "1,999", period: "/month", description: "For small schools", maxUsers: 50, popular: false,
      features: ["Up to 50 users", "All leave types", "Analytics dashboard", "Leave balance tracking", "In-app notifications", "Priority email support"] },
    { id: "professional_monthly", name: "Professional", price: "4,999", period: "/month", description: "For growing schools", maxUsers: 500, popular: true,
      features: ["Up to 500 users", "Everything in Starter", "Advanced reports & export", "Department management", "Custom leave policies", "Phone & email support", "Bulk user import"] },
    { id: "enterprise_monthly", name: "Enterprise", price: "14,999", period: "/month", description: "For school chains & trusts", maxUsers: 10000, popular: false,
      features: ["Up to 10,000 users", "Everything in Professional", "Multi-branch support", "Dedicated account manager", "Custom integrations", "SLA guarantee (99.9%)", "On-premise option", "24/7 priority support"] }
  ],
  annual: [
    { id: "trial", name: "Trial", price: "Free", period: "7 days", description: "Try Ariskool risk-free", maxUsers: 10, popular: false,
      features: ["Up to 10 users", "Leave request & approval", "Basic dashboard", "Email support"] },
    { id: "starter_annual", name: "Starter", price: "19,190", originalPrice: "23,988", period: "/year", savings: "Save Rs.4,798", description: "For small schools", maxUsers: 50, popular: false,
      features: ["Up to 50 users", "All leave types", "Analytics dashboard", "Leave balance tracking", "In-app notifications", "Priority email support"] },
    { id: "professional_annual", name: "Professional", price: "47,990", originalPrice: "59,988", period: "/year", savings: "Save Rs.11,998", description: "For growing schools", maxUsers: 500, popular: true,
      features: ["Up to 500 users", "Everything in Starter", "Advanced reports & export", "Department management", "Custom leave policies", "Phone & email support", "Bulk user import"] },
    { id: "enterprise_annual", name: "Enterprise", price: "1,43,990", originalPrice: "1,79,988", period: "/year", savings: "Save Rs.35,998", description: "For school chains & trusts", maxUsers: 10000, popular: false,
      features: ["Up to 10,000 users", "Everything in Professional", "Multi-branch support", "Dedicated account manager", "Custom integrations", "SLA guarantee (99.9%)", "On-premise option", "24/7 priority support"] }
  ]
};

export const Pricing = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly');
  const plans = allPlans[billing];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="pricing-page">
        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-3">Pricing</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4" data-testid="pricing-title">
                Simple, transparent pricing
              </h1>
              <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10">
                Choose the plan that fits your school. All plans include a 7-day free trial. No credit card required.
              </p>
              <div className="inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
                <button
                  onClick={() => setBilling('monthly')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="billing-monthly-btn"
                >Monthly</button>
                <button
                  onClick={() => setBilling('annual')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${billing === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="billing-annual-btn"
                >
                  Annual
                  <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">Save 20%</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:shadow-xl ${
                    plan.popular
                      ? 'border-teal-500 shadow-lg shadow-teal-100 bg-white ring-1 ring-teal-500'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3" /> Most Popular
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{plan.name}</h3>
                    <p className="text-sm text-slate-500">{plan.description}</p>
                    <div className="mt-4">
                      {plan.originalPrice && (
                        <span className="text-lg text-slate-300 line-through mr-2">Rs.{plan.originalPrice}</span>
                      )}
                      <span className="text-4xl font-extrabold text-slate-900">
                        {plan.price === "Free" ? "Free" : `Rs.${plan.price}`}
                      </span>
                      {plan.price !== "Free" && (
                        <span className="text-slate-400 text-sm ml-1">{plan.period}</span>
                      )}
                    </div>
                    {plan.savings && (
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">
                          <BadgePercent className="h-3 w-3" /> {plan.savings}
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Up to {plan.maxUsers.toLocaleString()} users</p>
                  </div>
                  <ul className="space-y-3 flex-1 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full rounded-xl font-semibold ${
                      plan.popular
                        ? 'bg-teal-600 hover:bg-teal-500 text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                    }`}
                    onClick={() => navigate('/register')}
                    data-testid={`plan-${plan.id}-btn`}
                  >
                    {plan.price === "Free" ? "Start Free Trial" : "Get Started"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center text-sm text-slate-500">
              <p>All prices are in INR and exclude GST (18%).</p>
              <p className="mt-1">
                Need a custom plan for your school chain?{' '}
                <a href="/contact" className="text-teal-600 hover:underline font-medium">Contact our sales team</a>
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Plan Comparison</h2>
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
              <div className="w-full text-sm bg-white" data-testid="comparison-table">
                <div className="grid grid-cols-5 bg-slate-900 text-white">
                  <div className="text-left p-4 font-semibold">Feature</div>
                  <div className="text-center p-4 font-semibold">Trial</div>
                  <div className="text-center p-4 font-semibold">Starter</div>
                  <div className="text-center p-4 font-semibold bg-teal-700">Professional</div>
                  <div className="text-center p-4 font-semibold">Enterprise</div>
                </div>
                {[
                  { feature: 'Max Users', trial: '10', starter: '50', pro: '500', enterprise: '10,000' },
                  { feature: 'Leave Types', trial: '3 basic', starter: 'All', pro: 'All + Custom', enterprise: 'Unlimited' },
                  { feature: 'Analytics Dashboard', trial: 'Basic', starter: 'Standard', pro: 'Advanced', enterprise: 'Advanced + API' },
                  { feature: 'Report Export (PDF/CSV)', trial: '-', starter: 'Yes', pro: 'Yes', enterprise: 'Yes' },
                  { feature: 'In-App Notifications', trial: 'Yes', starter: 'Yes', pro: 'Yes', enterprise: 'Yes' },
                  { feature: 'Custom Leave Policies', trial: '-', starter: '-', pro: 'Yes', enterprise: 'Yes' },
                  { feature: 'Bulk User Import', trial: '-', starter: '-', pro: 'Yes', enterprise: 'Yes' },
                  { feature: 'Multi-Branch Support', trial: '-', starter: '-', pro: '-', enterprise: 'Yes' },
                  { feature: 'Support', trial: 'Email', starter: 'Priority', pro: 'Phone + Email', enterprise: '24/7 Priority' },
                ].map((row, i) => (
                  <div key={i} className={`grid grid-cols-5 border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                    <div className="p-4 font-medium text-slate-900">{row.feature}</div>
                    <div className="p-4 text-center text-slate-500">{row.trial}</div>
                    <div className="p-4 text-center text-slate-500">{row.starter}</div>
                    <div className="p-4 text-center text-teal-700 font-medium bg-teal-50/30">{row.pro}</div>
                    <div className="p-4 text-center text-slate-500">{row.enterprise}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {[
                { q: 'Can I switch between monthly and annual billing?', a: "Yes! Switch from monthly to annual at any time to start saving 20%. We'll prorate the remaining balance." },
                { q: 'Can I change my plan later?', a: 'Absolutely. Upgrade or downgrade anytime. Changes take effect at the next billing cycle.' },
                { q: 'What payment methods do you accept?', a: 'We accept UPI, all major credit/debit cards, and net banking via Stripe India.' },
                { q: 'Do you offer discounts for government schools?', a: 'Yes, we offer special pricing for government, aided, and non-profit schools. Contact us for details.' },
                { q: 'Is GST included in the price?', a: 'No, all prices are exclusive of GST (18%). GST is added at checkout per Indian tax regulations.' },
              ].map((faq, i) => (
                <div key={i} className="p-5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Pricing;
